import http from '../../services/httpClient';

/**
 * Every call the Not-in-Menu worklist makes, normalised to
 *   { ok: true, ...body }  |  { ok: false, status, code, error, shortages, mismatches, dishes }
 * (the utils/banquetApi.js pattern). httpClient throws for 4xx/5xx but RETURNS
 * a 403 as { success: false } — so success must be checked explicitly, or a
 * denied write would read as done. Successful POSTs already fire the
 * app-wide data-changed signal inside httpClient.
 */
const failure = (status, data, fallback) => ({
  ok: false,
  status,
  code: (data && data.code) || 'REQUEST_FAILED',
  error: (data && data.error) || fallback,
  shortages: data && Array.isArray(data.shortages) ? data.shortages : null,
  mismatches: data && Array.isArray(data.mismatches) ? data.mismatches : null,
  dishes: data && Array.isArray(data.dishes) ? data.dishes : null,
});

const settle = async (call, fallback) => {
  try {
    const res = await call();
    if (!res || res.success !== true) return failure(403, res, fallback);
    return { ok: true, ...res };
  } catch (err) {
    return failure(err?.status || 0, { ...(err?.data || {}), error: err?.data?.error || err?.message }, fallback);
  }
};

const enc = encodeURIComponent;

export const getPending = () => settle(() => http.get('/tsr-unmapped/pending'), 'Could not load Not-in-Menu items.');
export const getMenuItems = () => settle(() => http.get('/menu/items?language=en&includeInactive=true'), 'Could not load the menu.');
// `group`: 'sales' (regular + online lines) or 'banquet' — a banquet's lines are kept
// apart (spec 2026-10-26-tsr-add-as-not-in-menu); the server treats a missing group as 'sales'.
export const previewDish = (groupKey, menuItemId, group = 'sales') =>
  settle(() => http.get(`/tsr-unmapped/preview?groupKey=${enc(groupKey)}&menuItemId=${menuItemId}&group=${enc(group)}`), 'Could not preview this dish.');
export const confirmDish = ({ groupKey, menuItemId, allowNegativeStock = false, group = 'sales' }) =>
  settle(() => http.post('/tsr-unmapped/confirm', { groupKey, menuItemId, allowNegativeStock: allowNegativeStock === true, group }), 'Could not confirm this dish.');
export const confirmDishBatch = ({ items, allowNegativeStock = false }) =>
  settle(() => http.post('/tsr-unmapped/confirm-batch', { items, allowNegativeStock: allowNegativeStock === true }), 'Could not confirm these dishes.');
export const undoPreview = (groupKey, group = 'sales') =>
  settle(() => http.get(`/tsr-unmapped/undo-preview?groupKey=${enc(groupKey)}&group=${enc(group)}`), 'Could not check the undo.');
export const undoDish = (groupKey, group = 'sales') => settle(() => http.post('/tsr-unmapped/undo', { groupKey, group }), 'Could not undo this dish.');
export const skipDish = (groupKey, unskip = false, group = 'sales') =>
  settle(() => http.post('/tsr-unmapped/skip', { itemName: groupKey, unskip, group }), unskip ? 'Could not unskip this dish.' : 'Could not skip this dish.');
export const checkGate = (through) =>
  settle(() => http.get(`/tsr-unmapped/gate${through ? `?through=${through}` : ''}`), 'Could not check unmapped sales.');
